import React, { useState, useEffect } from "react";
import axios from "axios";
import Sidebar from "../component/Sidebar";
import MobileHeaderToggle from "../component/MobileHeaderToggle";
import { Key, RefreshCw, Save, Edit3, XCircle, Eye, EyeOff } from "lucide-react";
import toast from "react-hot-toast";

const normalizeEmployeeId = (value) => String(value || "").trim().toUpperCase();
const isExEmployeeId = (value) => normalizeEmployeeId(value).startsWith("EX-");
const getEmployeePrefix = (value) => {
  const normalized = normalizeEmployeeId(value);
  if (!normalized || isExEmployeeId(normalized)) return "";
  return normalized.split("-")[0] || "";
};

const EmployeeUserIdCreated = () => {
  const [dataList, setDataList] = useState([]);
  const [bulkPassword, setBulkPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [selectedIds, setSelectedIds] = useState([]);
  const [editingRow, setEditingRow] = useState(null);
  const [tempPassword, setTempPassword] = useState("");
  const [visiblePasswords, setVisiblePasswords] = useState({});
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedEmployeePrefix, setSelectedEmployeePrefix] = useState("ALL");

  const token = localStorage.getItem("token");
  const API_URL = "/api/employee-ids";

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const res = await axios.get(API_URL, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setDataList((res.data || []).filter((emp) => !isExEmployeeId(emp.employeeId)));
    } catch (err) {
      toast.error("Error loading employees");
    }
  };

  const togglePasswordVisibility = (id) => {
    setVisiblePasswords((prev) => ({
      ...prev,
      [id]: !prev[id],
    }));
  };

  const normalizeSearchInput = (value) => {
    let v = String(value || "").toUpperCase();
    if (/^[A-Z]+\d/.test(v)) {
      v = v.replace(/^([A-Z]+)-?(\d.*)$/, "$1-$2");
    }
    return v;
  };

  const filteredDataList = dataList.filter((emp) => {
    if (isExEmployeeId(emp.employeeId)) return false;
    const prefixMatch =
      selectedEmployeePrefix === "ALL" ||
      getEmployeePrefix(emp.employeeId) === selectedEmployeePrefix;
    const q = String(searchTerm || "").toUpperCase().trim();
    if (!prefixMatch) return false;
    if (!q) return true;
    return (
      String(emp.name || "").toUpperCase().includes(q) ||
      String(emp.employeeUserId || "").toUpperCase().includes(q) ||
      String(emp.employeeId || "").toUpperCase().includes(q)
    );
  });
  const employeePrefixOptions = ["ALL", ...new Set(
    dataList.map((emp) => getEmployeePrefix(emp.employeeId)).filter(Boolean)
  )];

  const handleSelectAll = (e) => {
    if (e.target.checked) {
      setSelectedIds(filteredDataList.map((emp) => emp.employeeId));
    } else {
      setSelectedIds([]);
    }
  };

  const handleSelectOne = (id) => {
    setSelectedIds((prev) =>
      prev.includes(id) ? prev.filter((item) => item !== id) : [...prev, id]
    );
  };

  const handleBulkGenerate = async () => {
    if (!bulkPassword) return toast.error("Please enter a password first!");
    const targetEmployeeIds =
      selectedIds.length > 0 ? selectedIds : filteredDataList.map((emp) => emp.employeeId);
    if (targetEmployeeIds.length === 0) {
      toast.error("No active employees found for password generation");
      return;
    }
    const message = selectedIds.length > 0 
      ? `Update password for ${selectedIds.length} selected employees?`
      : `Update password for ${targetEmployeeIds.length} filtered employees?`;

    if (!window.confirm(message)) return;

    setLoading(true);
    try {
      await axios.post(`${API_URL}/generate-all-passwords`, 
        { 
          customPassword: bulkPassword,
          targetEmployeeIds
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success("Passwords generated successfully!");
      setBulkPassword("");
      setSelectedIds([]);
      fetchData();
    } catch (err) {
      toast.error("Update failed");
    } finally {
      setLoading(false);
    }
  };

  const startEdit = (emp) => {
    setEditingRow(emp.employeeId);
    setTempPassword(""); 
  };

  const saveIndividualPassword = async (emp) => {
    if (!tempPassword) return toast.error("Password cannot be empty");
    try {
      await axios.post(`${API_URL}/generate-all-passwords`, 
        { 
          customPassword: tempPassword,
          targetEmployeeIds: [emp.employeeId] 
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(`Password updated for ${emp.name}`);
      setEditingRow(null);
      fetchData();
    } catch (err) {
      toast.error("Failed to update individual password");
    }
  };

  return (
    <div className="flex h-screen flex-col md:flex-row">
      <Sidebar/>
    <div className="flex-1 flex flex-col min-h-0 overflow-hidden p-3">
        <h2 className="text-xl md:text-2xl font-bold text-dorika-blue mb-4">
          Employee Password Management
        </h2>

        <MobileHeaderToggle>
        {/* TOP SECTION */}
        <div className="bg-white p-4 md:p-6 rounded-2xl shadow-sm border-t-4 border-dorika-orange mb-6">
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4 items-end">
            <div className="flex flex-col">
              <label className="block text-sm font-bold text-gray-700 mb-2 font-mono uppercase">
                {selectedIds.length > 0 
                  ? `Target: (${selectedIds.length}) Selected` 
                  : "Enter Password"}
              </label>
              <input
                type="text"
                placeholder="Enter password to generate..."
                value={bulkPassword}
                onChange={(e) => setBulkPassword(e.target.value)}
                className="w-full border-2 border-gray-200 p-2 md:p-2 rounded-xl focus:border-dorika-blue outline-none transition-all"
              />
            </div>
            <div className="flex flex-col">
              <label className="block text-sm font-bold text-gray-700 mb-2 font-mono uppercase">
                Status
              </label>
              <select
                value={selectedEmployeePrefix}
                onChange={(e) => setSelectedEmployeePrefix(e.target.value)}
                className="w-full border-2 border-dorika-blue p-2 md:p-2 rounded-xl uppercase focus:border-dorika-orange outline-none transition-all bg-white"
              >
                {employeePrefixOptions.map((prefix) => (
                  <option key={prefix} value={prefix}>
                    {prefix}
                  </option>
                ))}
              </select>
            </div>
            <div className="flex flex-col">
              <label className="block text-sm font-bold text-gray-700 mb-2 font-mono uppercase">
                Search Employee
              </label>
              <input
                type="text"
                placeholder="Search by Name / User ID / Employee ID"
                value={searchTerm}
                onChange={(e) => setSearchTerm(normalizeSearchInput(e.target.value))}
                className="w-full border-2 border-dorika-blue p-2 md:p-2 rounded-xl uppercase focus:border-dorika-orange outline-none transition-all"
              />
            </div>
            <button
              onClick={handleBulkGenerate}
              disabled={loading}
              className="bg-dorika-blue hover:bg-black text-white px-6 md:px-8 py-3 md:py-3 rounded-xl font-bold flex items-center justify-center gap-2 transition-all disabled:opacity-50 h-[46px] mt-auto"
            >
              {loading ? <RefreshCw className="animate-spin" /> : <Key size={20} />}
              <span className="whitespace-nowrap">
                {selectedIds.length > 0 ? "Generate Selected" : "Generate All"}
              </span>
            </button>
          </div>
        </div>
        </MobileHeaderToggle>

        {/* TABLE SECTION */}
        {/* <div className="bg-white rounded-2xl shadow-md border border-gray-200 overflow-hidden"> */}
          <div className="w-full flex-1 min-h-0 overflow-auto">
            <table className="w-full text-left border-collapse min-w-[800px]">
              <thead className="bg-blue-50 text-dorika-blue text-[10px] md:text-xs uppercase font-bold sticky top-0 z-10">
                <tr>
                  <th className="p-2 md:p-4 border-b w-10">
                    <input 
                      type="checkbox" 
                      onChange={handleSelectAll}
                      checked={
                        filteredDataList.length > 0 &&
                        filteredDataList.every((emp) => selectedIds.includes(emp.employeeId))
                      }
                      className="w-4 h-4 accent-dorika-orange cursor-pointer"
                    />
                  </th>
                  <th className="p-2 md:p-4 border-b">Sl No</th>
                  <th className="p-2 md:p-4 border-b">Employee ID</th>
                  <th className="p-2 md:p-4 border-b">User ID</th>
                  <th className="p-2 md:p-4 border-b">Name</th>
                  <th className="p-2 md:p-4 border-b">Designation</th>
                  <th className="p-2 md:p-4 border-b">Password Status</th>
                  <th className="p-2 md:p-4 border-b text-center">Action</th>
                </tr>
              </thead>
              <tbody className="text-xs md:text-sm">
                {filteredDataList.map((emp, index) => (
                  <tr 
                    key={index} 
                    className={`${selectedIds.includes(emp.employeeId) ? 'bg-orange-50' : 'hover:bg-gray-50'} border-b last:border-0 transition-colors`}
                  >
                    <td className="p-2 md:p-4">
                      <input 
                        type="checkbox" 
                        checked={selectedIds.includes(emp.employeeId)}
                        onChange={() => handleSelectOne(emp.employeeId)}
                        className="w-4 h-4 accent-dorika-orange cursor-pointer"
                      />
                    </td>
                    <td className="p-2 md:p-4 text-gray-500">{index + 1}</td>
                    <td className="p-2 md:p-4 font-semibold text-gray-800">{emp.employeeId}</td>
                    <td className="p-2 md:p-4 font-bold text-dorika-blue">{emp.employeeUserId}</td>
                    <td className="p-2 md:p-4 font-medium uppercase">{emp.name}</td>
                    <td className="p-2 md:p-4 text-[10px] md:text-xs text-gray-600 truncate max-w-[150px]" title={emp.designation}>
                      {emp.designation}
                    </td>
                    <td className="p-2 md:p-4">
                      {editingRow === emp.employeeId ? (
                        <input 
                          type="text"
                          placeholder="Set Password"
                          value={tempPassword}
                          onChange={(e) => setTempPassword(e.target.value)}
                          className="border border-dorika-blue rounded px-2 py-1 w-full max-w-[120px] text-xs outline-none shadow-inner"
                          autoFocus
                        />
                      ) : (
                        <div className="flex items-center gap-2">
                          {/* STATUS BADGE LOGIC */}
                          <span className={`px-2 md:px-3 py-1 rounded-lg font-bold text-[10px] md:text-xs ${
                            !emp.hasPassword
                            ? "bg-red-50 text-red-500" 
                            : "bg-green-50 text-green-600"
                          }`}>
                            {!emp.hasPassword
                              ? "NONE" 
                              : (visiblePasswords[emp.employeeId] ? emp.plainPassword : "GENERATED")}
                          </span>
                          
                          {/* EYE ICON (Only if password exists) */}
                          {emp.hasPassword && (
                            <button 
                              onClick={() => togglePasswordVisibility(emp.employeeId)}
                              className="text-gray-400 hover:text-dorika-blue transition-colors"
                            >
                              {/* {visiblePasswords[emp.employeeId] ? <EyeOff size={14} /> : <Eye size={14} />} */}
                            </button>
                          )}
                        </div>
                      )}
                    </td>
                    <td className="p-2 md:p-4 text-center">
                      {editingRow === emp.employeeId ? (
                        <div className="flex justify-center gap-2">
                          <button onClick={() => saveIndividualPassword(emp)} className="text-green-600 hover:text-green-800">
                            <Save size={18} />
                          </button>
                          <button onClick={() => setEditingRow(null)} className="text-red-500 hover:text-red-700">
                            <XCircle size={18} />
                          </button>
                        </div>
                      ) : (
                        <button onClick={() => startEdit(emp)} className="text-dorika-blue hover:text-dorika-orange transition-colors">
                          <Edit3 size={18} />
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    // </div>
  );
};

export default EmployeeUserIdCreated;
